import type {
  ClientNotificationSetting,
  ClientNotificationSettingChange,
  ClientNotificationSettingsConflictResponse,
  ClientNotificationSettingsResponse,
  NotificationSettingSourceLayer,
} from "../../../shared/api-types";
import {
  EDITED_CHANNEL,
  MESSAGE_TYPES,
  messageTypeLabel,
  reminderTimeLabel,
} from "./notification-settings-client";

// The patient page's Notifications card (NO-851): what each of
// GET /api/patients/:patientId/notification-settings reads as, and the
// editor's draft arithmetic for the PUT (NO-848). The message types, their
// wording and the reminder time labels are NO-834's
// (notification-settings-client), so the card and the Notification settings
// page name everything the same way. Pure and relative-import only, so
// `tsx --test` runs it.

type View = ClientNotificationSettingsResponse;
type Row = ClientNotificationSetting;

export function patientNotificationsQueryKey(patientId: string): string[] {
  return ["patient-notification-settings", patientId];
}

export function patientNotificationsPath(patientId: string): string {
  return `/api/patients/${encodeURIComponent(patientId)}/notification-settings`;
}

/**
 * A background refetch would replace the values under an open editor, so the
 * card changes only through a save's answer, and nothing outlives the page.
 */
export const PATIENT_NOTIFICATIONS_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

// --- Labels ------------------------------------------------------------------

export const DEFAULT_UNKNOWN_LABEL =
  "Uses the default. You'll see its value after you save.";

const SOURCE_LABELS: Record<NotificationSettingSourceLayer, string> = {
  client: "Client's choice",
  rd: "Your default",
  practice: "Practice default",
  none: "No default set",
};

export function sourceLabel(layer: NotificationSettingSourceLayer): string {
  return SOURCE_LABELS[layer];
}

/** NO-890. The label for a Client choice the Client made themselves. */
export const SET_BY_CLIENT_LABEL = "Set by the Client";

/**
 * Where a row's value came from, as the RD reads it. A value from the Client's
 * layer says who entered it: the Client themselves, or (as before) the RD or
 * staff on their behalf. The other layers are labelled as before.
 */
export function rowSourceLabel(
  row: Row,
  layer: NotificationSettingSourceLayer
): string {
  if (layer === "client" && row.clientChoice?.source === "client")
    return SET_BY_CLIENT_LABEL;

  return sourceLabel(layer);
}

export function messageTypeDescription(kind: string): string {
  return MESSAGE_TYPES.find((type) => type.kind === kind)?.description ?? "";
}

/** "1 day and 1 hour before", longest first; "No time set" when there is none. */
export function timingSummary(offsets: readonly number[] | null): string {
  if (offsets === null || offsets.length === 0) return "No time set";

  const parts = [...offsets]
    .sort((left, right) => right - left)
    .map((minutes) => reminderTimeLabel(minutes).replace(/ before$/, ""));
  const head = parts.slice(0, -1).join(", ");
  const joined = head === "" ? parts[0] : `${head} and ${parts[parts.length - 1]}`;

  return `${joined} before`;
}

/** The reminder's time choices, shortest first, from the server's menu only. */
export function timingMenu(view: View): number[] {
  return [...view.timingMenuMinutes].sort((left, right) => left - right);
}

/** The rows the card shows: email only, in MESSAGE_TYPES order. */
export function cardRows(view: View): Row[] {
  return MESSAGE_TYPES.flatMap((type) =>
    view.settings.filter(
      (row) => row.kind === type.kind && row.channel === EDITED_CHANNEL
    )
  );
}

// --- The editor's draft ------------------------------------------------------

/** One row's Client values; null means "follow the default". */
export interface DraftRow {
  enabled: boolean | null;
  offsetsMinutes: number[] | null;
}

export type NotificationsDraft = Record<string, DraftRow>;

export const USE_DEFAULT: DraftRow = { enabled: null, offsetsMinutes: null };

export function rowKey(row: Pick<Row, "kind" | "channel">): string {
  return `${row.kind}|${row.channel}`;
}

/** Where a row starts: the Client's own values, else the default. */
export function startingRow(row: Row): DraftRow {
  const choice = row.clientChoice;
  if (choice === null) return USE_DEFAULT;

  return {
    enabled: choice.enabled,
    offsetsMinutes: row.timed ? choice.offsetsMinutes : null,
  };
}

export function startingDraft(view: View): NotificationsDraft {
  return Object.fromEntries(
    cardRows(view).map((row) => [rowKey(row), startingRow(row)])
  );
}

/**
 * What the Client gets from this row as drafted. A field the draft leaves to
 * the default shows the server's resolved value; the value behind a Client's
 * own one is not in the answer, so it shows once a save returns.
 */
export function shownRow(
  row: Row,
  draft: DraftRow
): { enabled: boolean; offsetsMinutes: number[] } {
  return {
    enabled: draft.enabled ?? row.enabled,
    offsetsMinutes: draft.offsetsMinutes ?? resolvedTimes(row),
  };
}

function resolvedTimes(row: Row): number[] {
  return row.offsetsMinutes ?? [];
}

export function followsDefault(draft: DraftRow): boolean {
  return draft.enabled === null && draft.offsetsMinutes === null;
}

function sameTimes(left: readonly number[], right: readonly number[]): boolean {
  return left.length === right.length && left.every((value) => right.includes(value));
}

function settledEnabled(row: Row, start: DraftRow, draft: DraftRow): boolean | null {
  return start.enabled === null && draft.enabled === row.enabled ? null : draft.enabled;
}

function settledTimes(row: Row, start: DraftRow, draft: DraftRow): number[] | null {
  const drafted = draft.offsetsMinutes;
  if (start.offsetsMinutes !== null || drafted === null) return drafted;

  return sameTimes(drafted, resolvedTimes(row)) ? null : drafted;
}

/**
 * The draft as it would save. A field the Client had no value for, drafted
 * back to the value it inherits (toggled off and on, a time unchecked and
 * rechecked), is still the default: it sends null and is no change, so an
 * undone edit never pins the default's value as the Client's own.
 */
export function settledRow(row: Row, draft: DraftRow): DraftRow {
  const start = startingRow(row);

  return {
    enabled: settledEnabled(row, start, draft),
    offsetsMinutes: settledTimes(row, start, draft),
  };
}

/** Does the row, as drafted, leave both fields to the default? */
export function draftFollowsDefault(row: Row, draft: DraftRow): boolean {
  return followsDefault(settledRow(row, draft));
}

/**
 * A row the Client had their own value for, drafted back to the default. The
 * read answers only the Client's value, not the one behind it, so the value
 * this row would get is unknown until the save returns it.
 */
export function defaultValueUnknown(row: Row, draft: DraftRow): boolean {
  return draftFollowsDefault(row, draft) && !followsDefault(startingRow(row));
}

/** The edit view's source line for a row as drafted. */
export function draftSourceLabel(row: Row, draft: DraftRow): string {
  if (!draftFollowsDefault(row, draft)) {
    // An untouched Client-made choice still reads "Set by the Client"; once
    // the RD changes it, it is their entry.
    return sameRow(draft, startingRow(row))
      ? rowSourceLabel(row, "client")
      : sourceLabel("client");
  }

  return defaultValueUnknown(row, draft)
    ? DEFAULT_UNKNOWN_LABEL
    : sourceLabel(row.enabledFrom);
}

export function withEnabled(draft: DraftRow, enabled: boolean): DraftRow {
  return { ...draft, enabled };
}

/**
 * The on/off checkbox's change. Leaving an unknown default sets the Client's
 * value again and takes back their own times, so the times the editor then
 * shows are the times it sends, never the hidden default's.
 */
export function toggledEnabled(row: Row, draft: DraftRow, enabled: boolean): DraftRow {
  if (!defaultValueUnknown(row, draft)) return withEnabled(draft, enabled);

  return { enabled, offsetsMinutes: startingRow(row).offsetsMinutes };
}

/** Adds or removes one reminder time, starting from what the row shows now. */
export function withReminderTime(
  draft: DraftRow,
  shown: readonly number[],
  minutes: number,
  checked: boolean
): DraftRow {
  const others = shown.filter((value) => value !== minutes);
  const next = checked ? [...others, minutes] : others;

  return { ...draft, offsetsMinutes: next.sort((left, right) => right - left) };
}

/** NO-829 refuses an empty list; no times chosen sends as "follow the default". */
function sentTimes(row: Row, offsets: number[] | null): number[] | null {
  if (!row.timed) return null;

  return offsets !== null && offsets.length === 0 ? null : offsets;
}

function sentRow(row: Row, draft: DraftRow): DraftRow {
  const settled = settledRow(row, draft);

  return {
    enabled: settled.enabled,
    offsetsMinutes: sentTimes(row, settled.offsetsMinutes),
  };
}

function sameRow(left: DraftRow, right: DraftRow): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Every row the editor changed, whole (a change replaces the Client's row),
 * each naming the version it was read at.
 */
export function draftChanges(
  view: View,
  draft: NotificationsDraft
): ClientNotificationSettingChange[] {
  return cardRows(view).flatMap((row) => {
    const drafted = draft[rowKey(row)];
    if (!drafted) return [];

    const sent = sentRow(row, drafted);
    if (sameRow(sent, sentRow(row, startingRow(row)))) return [];

    return [
      {
        kind: row.kind,
        channel: row.channel,
        enabled: sent.enabled,
        offsetsMinutes: sent.offsetsMinutes,
        expectedVersion: row.version,
      },
    ];
  });
}

export const NO_REMINDER_TIME_MESSAGE = "Choose at least one reminder time.";

/**
 * A reminder drafted on with no time chosen; null when the draft can save. A
 * row whose default is unknown has no value to check.
 */
export function draftProblem(view: View, draft: NotificationsDraft): string | null {
  const missingTime = cardRows(view).some((row) => {
    const drafted = draft[rowKey(row)];
    if (!row.timed || !drafted || defaultValueUnknown(row, drafted)) return false;

    const shown = shownRow(row, drafted);

    return shown.enabled && shown.offsetsMinutes.length === 0;
  });

  return missingTime ? NO_REMINDER_TIME_MESSAGE : null;
}

/**
 * Rows someone else saved while this editor was open: a row this save did
 * not send whose version rose. The card then shows their values.
 */
export function changedByOthers(
  opened: View,
  saved: View,
  sent: readonly ClientNotificationSettingChange[]
): string[] {
  const sentKeys = new Set(sent.map(rowKey));
  const openedVersions = new Map(
    opened.settings.map((row) => [rowKey(row), row.version])
  );

  return cardRows(saved)
    .filter((row) => !sentKeys.has(rowKey(row)))
    .filter((row) => row.version > (openedVersions.get(rowKey(row)) ?? 0))
    .map((row) => messageTypeLabel(row.kind));
}

// --- Save outcomes -----------------------------------------------------------

interface ErrorCarrier {
  status?: unknown;
  body?: unknown;
}

function refusalCode(error: unknown, status: number): unknown {
  const carrier = (error ?? {}) as ErrorCarrier;
  if (carrier.status !== status) return undefined;

  const body = carrier.body;

  return typeof body === "object" && body !== null
    ? (body as { code?: unknown }).code
    : undefined;
}

/** The current choices a 409 carried, or null when the error is something else. */
export function conflictCurrent(error: unknown): View | null {
  if (refusalCode(error, 409) !== "version_conflict") return null;

  const body = (error as { body: Partial<ClientNotificationSettingsConflictResponse> })
    .body;

  return body.current ?? null;
}

/** The RD's own record is retired (NO-695): retrying cannot fix it. */
export function isTombstonedRefusal(error: unknown): boolean {
  return refusalCode(error, 409) === "rd_tombstoned";
}

export type CardNotice =
  | { kind: "saved" }
  | { kind: "changed"; messageTypes: string[] }
  | { kind: "conflict" }
  | { kind: "tombstoned" }
  | { kind: "failed" };

export function noticeAfterSave(
  opened: View,
  saved: View,
  sent: readonly ClientNotificationSettingChange[]
): CardNotice {
  const others = changedByOthers(opened, saved, sent);

  return others.length === 0
    ? { kind: "saved" }
    : { kind: "changed", messageTypes: others };
}

/**
 * A refused save. A conflict replaces the card with the server's current
 * choices and closes the editor; anything else keeps the draft open.
 */
export function failedSave(error: unknown): {
  notice: CardNotice;
  current: View | null;
} {
  const current = conflictCurrent(error);
  if (current !== null) return { notice: { kind: "conflict" }, current };

  if (isTombstonedRefusal(error))
    return { notice: { kind: "tombstoned" }, current: null };

  return { notice: { kind: "failed" }, current: null };
}
